import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { Target, Heart, CaretDown, CaretUp } from "@phosphor-icons/react";

const MOTIVATION_LABELS = [
  "More confidence", "More energy", "Better relationship", "Better father",
  "Better husband/partner", "Better mental health", "More attractive",
  "Better sex life", "More self-respect", "Better performance at work",
  "Comfortable in photos", "Comfortable at the beach",
];

export default function ClientGoalsView({ clientId }) {
  const [goals, setGoals] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/coach/clients/${clientId}/goals`);
        setGoals(data || null);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoaded(true);
      }
    })();
  }, [clientId]);

  if (!loaded) {
    return (
      <div className="id-card" data-testid="coach-goals-view-loading">
        <div className="id-label">Goals & Why</div>
        <div className="text-zinc-500 text-sm mt-2">Loading...</div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="id-card" data-testid="coach-goals-view-error">
        <div className="id-label mb-2">Goals & Why</div>
        <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333]">{err}</div>
      </div>
    );
  }

  if (!goals) {
    return (
      <div className="id-card" data-testid="coach-goals-view-empty">
        <div className="flex items-center gap-2 id-label mb-2">
          <Target size={14} weight="bold" /> Goals & Why
        </div>
        <div className="text-zinc-500 font-manrope text-sm">
          Client hasn&apos;t completed the goals questionnaire yet.
        </div>
      </div>
    );
  }

  const wLoss = (goals.current_weight_kg - goals.goal_weight_kg).toFixed(1);
  const waistLoss = (goals.current_waist_cm - goals.goal_waist_cm).toFixed(1);
  const allIn = goals.all_in;
  const checkedMotivations = (goals.motivations || []);

  return (
    <div className="id-card" data-testid="coach-goals-view">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
        data-testid="coach-goals-toggle"
      >
        <div className="flex items-center gap-2 id-label">
          <Target size={14} weight="bold" /> Goals & Why
          {allIn ? (
            <span className="ml-2 px-2 py-0.5 border border-[#FF3333] text-[#FF3333] text-[10px] font-bold tracking-[0.2em]">ALL IN</span>
          ) : (
            <span className="ml-2 px-2 py-0.5 border border-zinc-600 text-zinc-400 text-[10px] font-bold tracking-[0.2em]">NOT READY</span>
          )}
        </div>
        {open ? <CaretUp size={16} weight="bold" className="text-zinc-500" /> : <CaretDown size={16} weight="bold" className="text-zinc-500" />}
      </button>

      {open && (
        <div className="mt-4 space-y-5">
          {/* Numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <NumStat label="Current Wt" value={`${goals.current_weight_kg} kg`} testid="goals-cw" />
            <NumStat label="Goal Wt" value={`${goals.goal_weight_kg} kg`} accent testid="goals-gw" />
            <NumStat label="Wt to lose" value={`${wLoss} kg`} testid="goals-wt-loss" />
            <NumStat label="12mo target" value={`${goals.target_loss_12mo_kg} kg`} testid="goals-12mo" />
            <NumStat label="Current Waist" value={`${goals.current_waist_cm} cm`} testid="goals-cwaist" />
            <NumStat label="Goal Waist" value={`${goals.goal_waist_cm} cm`} accent testid="goals-gwaist" />
            <NumStat label="Waist to lose" value={`${waistLoss} cm`} testid="goals-waist-loss" />
            <NumStat
              label="Commitment"
              value={`${goals.commitment}/10`}
              accent={goals.commitment >= 8}
              testid="goals-commitment"
            />
          </div>

          {/* The real questions */}
          <Section title="The real questions">
            <Q label="Main driving force" value={goals.main_driving_force} testid="goals-driving-force" />
            <Q label="Biggest thing weight is stopping you doing" value={goals.biggest_stop} testid="goals-stop" />
            <Q label="What frustrates you most about your health" value={goals.biggest_frustration} testid="goals-frust" />
            <Q label="What have you missed out on" value={goals.missed_out_on} testid="goals-missed" />
            <Q label="If nothing changes in 12 months" value={goals.no_change_outcome} testid="goals-nochange" />
            <Q label="If you successfully lose the weight" value={goals.success_outcome} testid="goals-success" />
          </Section>

          {/* Motivations */}
          {(checkedMotivations.length > 0 || goals.motivations_other) && (
            <Section title="What gets better when you win">
              <div className="flex flex-wrap gap-2" data-testid="goals-motivations">
                {MOTIVATION_LABELS.filter((m) => checkedMotivations.includes(m)).map((m) => (
                  <span key={m} className="px-3 py-1 border border-[#FF3333] bg-[#FF3333]/10 text-xs font-manrope text-white">
                    {m}
                  </span>
                ))}
                {goals.motivations_other && (
                  <span className="px-3 py-1 border border-[#FF3333] bg-[#FF3333]/10 text-xs font-manrope text-white">
                    + {goals.motivations_other}
                  </span>
                )}
              </div>
            </Section>
          )}

          {/* Why now */}
          <Section title="Why now?">
            <Q label="Why is now the time" value={goals.why_now} testid="goals-whynow" />
            <Q label="Why not 6 months ago" value={goals.why_not_6mo} testid="goals-why6" />
            <Q label="Why not next year" value={goals.why_not_next_year} testid="goals-whynextyr" />
            <Q label="What makes this attempt different" value={goals.what_makes_different} testid="goals-different" />
          </Section>

          {/* Final commitment */}
          <div className={`border ${allIn ? "border-[#FF3333]" : "border-zinc-700"} bg-[#0F0F0F] px-4 py-4`} data-testid="goals-allin-card">
            <div className="flex items-center gap-2">
              <Heart size={18} weight="fill" className={allIn ? "text-[#FF3333]" : "text-zinc-500"} />
              <div className="id-label">Are you all in?</div>
            </div>
            <div className={`font-oswald text-2xl uppercase tracking-wide mt-1 ${allIn ? "text-white" : "text-zinc-400"}`}>
              {allIn ? "YES — Ready to stop starting over." : "NO — Not ready to commit yet."}
            </div>
          </div>

          {goals.updated_at && (
            <div className="text-[10px] text-zinc-600 uppercase tracking-[0.15em] font-manrope">
              Submitted {new Date(goals.created_at || goals.updated_at).toLocaleDateString()}
              {goals.updated_at && goals.created_at && goals.updated_at !== goals.created_at && (
                <> · Updated {new Date(goals.updated_at).toLocaleDateString()}</>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NumStat({ label, value, accent, testid }) {
  return (
    <div className={`border ${accent ? "border-[#FF3333]" : "border-[#262626]"} px-3 py-2`} data-testid={testid}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold">{label}</div>
      <div className={`font-oswald text-xl font-bold ${accent ? "text-[#FF3333]" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="id-label mb-3 text-zinc-400">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Q({ label, value, testid }) {
  if (!value || !String(value).trim()) return null;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold mb-1">{label}</div>
      <p className="text-zinc-200 text-sm font-manrope leading-relaxed whitespace-pre-wrap">{value}</p>
    </div>
  );
}
